import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import QuoteComments from './QuoteComments';
import { useAnalytics } from '../hooks/useAnalytics';

export default function QuoteDetail() {
  const { logEvent } = useAnalytics();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [business, setBusiness] = useState(null);
  const [quote, setQuote] = useState({
    quote_number: '',
    client_id: '',
    quote_date: new Date().toISOString().split('T')[0],
    expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'Draft',
    subtotal: 0,
    tax_rate: 20,
    tax_amount: 0,
    total: 0,
    notes: '',
    terms: '',
    items: [],
  });
  const [publicQuoteData, setPublicQuoteData] = useState(null);

  useEffect(() => {
    if (user) {
      fetchClients();
      fetchBusinessProfile();
      if (!isNew) {
        fetchQuote();
      } else {
        generateQuoteNumber();
      }
    }
  }, [user, id]);

  useEffect(() => {
    if (user && !isNew) {
      fetchPublicQuote();
    }
  }, [user, id]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('client_name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
    }
  };

  const fetchBusinessProfile = async () => {
    const { data: business, error: businessError } = await supabase
      .from('business_profile')
      .select('business_name')
      .eq('user_id', user.id) 
      .single();

    if (businessError) {
      console.error('Error fetching business profile:', businessError);
      setError('Failed to load business profile');
    } else {
      setBusiness(business);
    }
  };

  const fetchQuote = async () => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          clients (
            client_id,
            client_name,
            client_email
          ),
          items:quote_items (*)
        `)
        .eq('quote_id', id)
        .single();

      if (error) throw error;
      setQuote({
        ...data,
        quote_date: data.quote_date?.split('T')[0],
        expiry_date: data.expiry_date?.split('T')[0],
        items: data.items || [],
      });
      fetchBusinessProfile();
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const generateQuoteNumber = async () => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('quote_number')
        .order('created_at', { ascending: false })
        .limit(1);
      logEvent('QuoteDetail', 'Generate Quote Number', 'Quote number generated');
      if (error) throw error;

      let nextNumber = 1;
      if (data && data.length > 0 && data[0].quote_number) {
        const lastNumber = parseInt(data[0].quote_number.split('-')[1]);
        nextNumber = lastNumber + 1;
      }

      setQuote(prev => ({
        ...prev,
        quote_number: `QT-${String(nextNumber).padStart(4, '0')}`
      }));
    } catch (error) {
      console.error('Error generating quote number:', error);
      setError('Failed to generate quote number');
    }
  };

  const fetchPublicQuote = async () => {
    try {
      const { data, error } = await supabase
        .from('public_quotes')
        .select('*')
        .eq('quote_id', id)
        .single();
      logEvent('QuoteDetail', 'Fetch Public Quote', `Quote ID: ${id}`);
      if (error && error.code !== 'PGRST116') throw error;

      setPublicQuoteData(data);
    } catch (error) {
      console.error('Error fetching public quote:', error);
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax_amount = (subtotal * quote.tax_rate) / 100;
    const total = subtotal + tax_amount;
    return { subtotal, tax_amount, total };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuote(prev => {
      const newQuote = {
        ...prev,
        [name]: value
      };

      // Recalculate totals if tax_rate changes
      if (name === 'tax_rate') {
        const totals = calculateTotals(prev.items);
        return {
          ...newQuote,
          ...totals
        };
      }

      return newQuote;
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...quote.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    
    const totals = calculateTotals(newItems);
    setQuote(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleAddItem = () => {
    const newItems = [
      ...quote.items,
      { description: '', quantity: 1, unit_price: 0 }
    ];
    
    const totals = calculateTotals(newItems);
    setQuote(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleRemoveItem = (index) => {
    const newItems = quote.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems);
    setQuote(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        // Create quote
        const { data: newQuote, error: quoteError } = await supabase
          .from('quotes')
          .insert([{
            ...quote,
            user_id: user.id,
            items: undefined, // Remove items from quote insert
          }])
          .select()
          .single();

        logEvent('QuoteDetail', 'Create Quote', `Quote ID: ${newQuote.quote_id}`);
        if (quoteError) throw quoteError;

        // Create quote items
        const items = quote.items.map(item => ({
          quote_id: newQuote.quote_id,
          user_id: user.id,
          ...item
        }));

        const { error: itemsError } = await supabase
          .from('quote_items')
          .insert(items);

        if (itemsError) throw itemsError;
        logEvent('QuoteDetail', 'Create Quote Items', `Quote ID: ${newQuote.quote_id}`);
        navigate(`/quotes/${newQuote.quote_id}`);
      } else {
        // Update quote
        const { error: quoteError } = await supabase
          .from('quotes')
          .update({
            ...quote,
            items: undefined, // Remove items from quote update
          })
          .eq('quote_id', id);
        logEvent('QuoteDetail', 'Update Quote', `Quote ID: ${id}`);
        if (quoteError) throw quoteError;

        // Delete existing items
        const { error: deleteError } = await supabase
          .from('quote_items')
          .delete()
          .eq('quote_id', id);
        logEvent('QuoteDetail', 'Delete Quote Items', `Quote ID: ${id}`);
        if (deleteError) throw deleteError;

        // Create new items
        const items = quote.items.map(item => ({
          quote_id: id,
          user_id: user.id,
          ...item
        }));

        const { error: itemsError } = await supabase
          .from('quote_items')
          .insert(items);

        if (itemsError) throw itemsError;
        logEvent('QuoteDetail', 'Create Quote Items', `Quote ID: ${id}`);
        await fetchQuote();
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      setError('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    try {
      setSaving(true);
      let shareableQuote;

      // Check if a public quote already exists
      const { data: existingQuote, error: fetchError } = await supabase
        .from('public_quotes')
        .select('*')
        .eq('quote_id', id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        throw fetchError;
      }

      if (existingQuote) {
        shareableQuote = existingQuote;
      } else {
        // Create new public quote
        const { data: newQuote, error: createError } = await supabase
          .from('public_quotes')
          .insert({
            quote_id: id,
            user_id: user.id,
            customization_options: {
              showPrices: true,
              showLineItems: true,
              allowComments: true,
            },
          })
          .select()
          .single();

        if (createError) throw createError;
        logEvent('QuoteDetail', 'Create Public Quote', `Quote ID: ${id}`);
        shareableQuote = newQuote;
      }

      // Get business name
      const { data: businessData, error: businessError } = await supabase
        .from('business_profile')
        .select('business_name')
        .eq('user_id', user.id)
        .single();

      if (businessError) throw businessError;

      // Generate shareable link
      const shareableLink = `${window.location.origin}/quote/${shareableQuote.id}`;

      // Update quote status to "Sent"
      const { error: updateError } = await supabase
        .from('quotes')
        .update({ status: 'Sent' })
        .eq('quote_id', id);

      if (updateError) throw updateError;
      logEvent('QuoteDetail', 'Update Quote Status', `Quote ID: ${id}`);
      // Copy link to clipboard
      await navigator.clipboard.writeText(shareableLink);
      alert(`Quote link copied to clipboard! Share it with ${businessData?.business_name ? businessData.business_name + "'s" : 'your'} client.`);

      // Update local state
      setQuote(prev => ({ ...prev, status: 'Sent' }));
      fetchPublicQuote();
    } catch (err) {
      console.error('Error sharing quote:', err);
      setError('Could not share the quote. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/quotes')}
            className="mr-4 text-gray-400 hover:text-gray-500"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>
              {isNew ? 'New Quote' : `Quote #${quote.quote_number}`}
            </h1>
            <p style={{ color: THEME.subtext }}>
              {isNew ? 'Create a new quote' : 'Edit quote details'}
            </p>
          </div>
        </div>
        {!isNew && (
          <div className="flex items-center space-x-4">
            {publicQuoteData && (
              <a
                href={`/quote/${publicQuoteData.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                View Public Quote
              </a>
            )}
            {publicQuoteData && (
              <a
                href={`/quotes/${publicQuoteData.quote_id}/interactions`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                Analytics
              </a>
            )}
            <button
              onClick={handleShare}
              className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
              style={{ 
                background: THEME.primary,
                boxShadow: shadows.md,
              }}
            >
              <ShareIcon className="w-5 h-5 mr-2" />
              Share Quote
            </button>
          </div>
        )}
      </div>

      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
        >
          {error}
        </div>
      )}

      {publicQuoteData && (
        <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: shadows.md }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
            Public Quote Settings
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Public Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={`${window.location.origin}/quote/${publicQuoteData.id}`}
                  readOnly
                  className="flex-1 rounded-lg border-gray-300 bg-gray-50"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/quote/${publicQuoteData.id}`);
                    alert('Link copied to clipboard!');
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg"
                  style={{ 
                    backgroundColor: `${THEME.primary}15`,
                    color: THEME.primary
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Client
              </label>
              <select
                name="client_id"
                value={quote.client_id}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Status
              </label>
              <select
                name="status"
                value={quote.status}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              >
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Accepted">Accepted</option>
                <option value="Declined">Declined</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Quote Date
              </label>
              <input
                type="date"
                name="quote_date"
                value={quote.quote_date}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Expiry Date
              </label>
              <input
                type="date"
                name="expiry_date"
                value={quote.expiry_date}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold" style={{ color: THEME.text }}>Items</h2>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: `${THEME.primary}15`,
                color: THEME.primary
              }}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Item
            </button>
          </div>

          <div className="space-y-4">
            {quote.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Item description"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                  />
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                    placeholder="Qty"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                    min="1"
                  />
                </div>
                <div className="w-32">
                  <input
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                    placeholder="Price"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="w-32 pt-2 text-right" style={{ color: THEME.text }}>
                  ${(item.quantity * item.unit_price).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="pt-2 text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}

            {quote.items.length === 0 && (
              <p className="text-center py-4" style={{ color: THEME.subtext }}>
                No items added yet. Click "Add Item" to get started.
              </p>
            )}

            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span style={{ color: THEME.subtext }}>Subtotal</span>
                    <span style={{ color: THEME.text }}>${quote.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span style={{ color: THEME.subtext }}>Tax Rate</span>
                    <input
                      type="number"
                      name="tax_rate"
                      value={quote.tax_rate}
                      onChange={handleInputChange}
                      className="w-20 text-right rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span style={{ color: THEME.subtext }}>%</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: THEME.subtext }}>Tax</span>
                    <span style={{ color: THEME.text }}>${quote.tax_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span style={{ color: THEME.text }}>Total</span>
                    <span style={{ color: THEME.primary }}>${quote.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Notes
              </label>
              <textarea
                name="notes"
                value={quote.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Add any notes for the client..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Terms & Conditions
              </label>
              <textarea
                name="terms"
                value={quote.terms}
                onChange={handleInputChange}
                rows={3}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Add your terms and conditions..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
            style={{ 
              backgroundColor: THEME.primary,
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Quote' : 'Save Changes')}
          </button>
        </div>
      </form>

      {!isNew && (
        <div className="mt-8 bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <h2 className="text-lg font-semibold mb-6" style={{ color: THEME.text }}>
            Comments
          </h2>
          <QuoteComments 
            quoteId={id} 
            user={user} 
            publicQuoteId={publicQuoteData?.id} 
            isPublicView={false}
            quoteOwnerId={quote?.user_id}
            businessName={business?.business_name}
          />
        </div>
      )}
    </div>
  );
} 